/**
 * MapPickerModal — generic location picker on a Leaflet map.
 * Centers on the given site, shows zones for context, lets the user
 * click anywhere on the map to drop / move a pin. Confirming returns
 * { lat, lng } to the caller.
 *
 * Props:
 *   site        — { lat, lng, name } to center on (required for sensible centering)
 *   zones       — array of { id, name, lat, lng } shown as reference markers (optional)
 *   initialLat  — pre-existing pin to start with (optional)
 *   initialLng  — same (optional)
 *   onPick({ lat, lng })  — confirm callback
 *   onClose     — cancel/close
 */
import React, { useEffect, useRef, useState } from 'react';
import { Map as MapIcon, X, Check, Trash2 } from 'lucide-react';
import C from '../../constants/colors';

export default function MapPickerModal({ site, zones, initialLat, initialLng, onPick, onClose }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  const [picked, setPicked] = useState(
    initialLat != null && initialLng != null
      ? { lat: initialLat, lng: initialLng }
      : null
  );

  // Anchor for initial centering: pin > site > fallback
  const center = (picked && [picked.lat, picked.lng])
    || (site?.lat != null && site?.lng != null && [site.lat, site.lng])
    || [25.2048, 55.2708]; // Dubai fallback

  useEffect(() => {
    function init() {
      if (!mapRef.current || mapInstance.current) return;
      const L = window.L;
      const map = L.map(mapRef.current).setView(center, site ? 18 : 12);
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY || ''}`, {
        attribution: '© CartoDB', maxZoom: 19,
      }).addTo(map);
      mapInstance.current = map;

      // Reference markers — site
      if (site?.lat != null && site?.lng != null) {
        L.circleMarker([site.lat, site.lng], {
          radius: 8, color: '#94a3b8', weight: 2, fillOpacity: 0.5, fillColor: '#94a3b8',
        }).addTo(map).bindTooltip(`Site: ${site.name}`);
      }

      // Reference markers — zones (clickable: pin to this zone)
      (zones || []).forEach(z => {
        if (z.lat == null || z.lng == null) return;
        const zm = L.circleMarker([z.lat, z.lng], {
          radius: 6, color: '#3b82f6', weight: 2, fillOpacity: 0.5, fillColor: '#3b82f6',
        }).addTo(map).bindTooltip(`Zone: ${z.name} (click to pin here)`);
        zm.on('click', () => {
          setPin(L, z.lat, z.lng);
        });
      });

      // Existing pin if any
      if (picked) setPin(L, picked.lat, picked.lng);

      // Click-to-pin on the map
      map.on('click', (e) => {
        setPin(L, e.latlng.lat, e.latlng.lng);
      });
    }

    function setPin(L, lat, lng) {
      if (markerRef.current) {
        mapInstance.current.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      const html = `<div style="width:20px;height:20px;border-radius:50%;background:#ef4444;border:3px solid #fff;box-shadow:0 0 0 4px rgba(239,68,68,0.35);"></div>`;
      const icon = L.divIcon({ html, className: '', iconSize: [20, 20], iconAnchor: [10, 10] });
      markerRef.current = L.marker([lat, lng], { icon }).addTo(mapInstance.current);
      setPicked({ lat, lng });
    }

    if (window.L) { init(); }
    else if (!document.getElementById('leaflet-script')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.id = 'leaflet-script';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = init;
      document.head.appendChild(script);
    } else {
      const t = setInterval(() => { if (window.L) { clearInterval(t); init(); } }, 80);
      return () => clearInterval(t);
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClearPin() {
    if (markerRef.current && mapInstance.current) {
      mapInstance.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    setPicked(null);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', width: '100%', maxWidth: '900px', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapIcon size={16} color={C.blue} />
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>Pin Location</div>
          <div style={{ fontSize: '11px', color: C.textMuted }}>
            Click on the map to drop a pin{(zones || []).length > 0 && ' or click a zone marker'}
          </div>
          {picked && (
            <div style={{ fontSize: '11px', color: C.green, fontFamily: 'JetBrains Mono, monospace', marginLeft: '8px' }}>
              {picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}
            </div>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* Map */}
        <div ref={mapRef} style={{ flex: 1, minHeight: 0 }} />

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {picked ? (
            <button onClick={handleClearPin}
              style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Trash2 size={13} />
              Clear pin
            </button>
          ) : (
            <div style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic' }}>
              No pin set yet.
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={onClose}
              style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={() => { onPick?.(picked); onClose?.(); }} disabled={!picked}
              style={{ padding: '8px 14px', borderRadius: '8px', border: 'none',
                background: picked ? C.blue : C.surfaceElevated, color: picked ? '#fff' : C.textMuted,
                fontSize: '12px', fontWeight: 700, cursor: picked ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Check size={13} />
              Use This Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
